import { useEffect, useState } from 'react';
import { getAdminRewardsData, saveRewardItem, processRedemption, GetAdminRewardsDataOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Loader2, Gift, ShoppingBag, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

type RewardsData = GetAdminRewardsDataOutputType;
type RewardItem = RewardsData['catalog'][0];
type Redemption = RewardsData['redemptions'][0];

const statusColor: Record<string, string> = {
  Pending: 'bg-yellow-100 text-yellow-800',
  Processing: 'bg-blue-100 text-blue-800',
  Fulfilled: 'bg-green-100 text-green-800',
  Cancelled: 'bg-muted text-muted-foreground',
};

function fmtDate(d?: string) {
  if (!d) return '';
  try { return format(parseISO(d), 'd MMM yyyy'); } catch { return ''; }
}

const blank = { name: '', description: '', pointsCost: 100, type: 'gift_card', active: true, imageUrl: '' };

export default function RewardsAdminTab() {
  const [data, setData] = useState<RewardsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editItem, setEditItem] = useState<RewardItem | null>(null);
  const [form, setForm] = useState({ ...blank });
  const [saving, setSaving] = useState(false);
  const [fulfillOpen, setFulfillOpen] = useState(false);
  const [fulfillTarget, setFulfillTarget] = useState<Redemption | null>(null);
  const [fulfillForm, setFulfillForm] = useState({ status: 'Fulfilled', voucherDetails: '', balanceCheckUrl: '', adminNotes: '' });
  const [fulfilling, setFulfilling] = useState(false);

  const load = () => {
    setLoading(true);
    getAdminRewardsData({}).then(setData).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditItem(null);
    setForm({ ...blank });
    setSheetOpen(true);
  };

  const openEdit = (item: RewardItem) => {
    setEditItem(item);
    setForm({ name: item.name, description: item.description || '', pointsCost: item.pointsCost, type: item.type || 'gift_card', active: item.active, imageUrl: item.imageUrl || '' });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await saveRewardItem({ id: editItem?.id, ...form });
      toast.success(editItem ? 'Reward updated' : 'Reward created');
      setSheetOpen(false);
      load();
    } catch (e: any) { toast.error(e.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const openFulfill = (r: Redemption) => {
    setFulfillTarget(r);
    setFulfillForm({ status: 'Fulfilled', voucherDetails: r.voucherDetails || '', balanceCheckUrl: r.balanceCheckUrl || '', adminNotes: r.adminNotes || '' });
    setFulfillOpen(true);
  };

  const handleFulfill = async () => {
    if (!fulfillTarget) return;
    setFulfilling(true);
    try {
      await processRedemption({ redemptionId: fulfillTarget.id, ...fulfillForm as any });
      toast.success('Redemption updated');
      setFulfillOpen(false);
      load();
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setFulfilling(false); }
  };

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>;

  const pending = data?.redemptions.filter(r => r.status === 'Pending' || r.status === 'Processing') || [];

  return (
    <div>
      <Tabs defaultValue="catalog">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="catalog"><Gift className="w-3.5 h-3.5 mr-1.5" /> Catalog ({data?.catalog.length ?? 0})</TabsTrigger>
            <TabsTrigger value="redemptions">
              <ShoppingBag className="w-3.5 h-3.5 mr-1.5" /> Redemptions
              {pending.length > 0 && <span className="ml-1.5 bg-primary text-primary-foreground text-xs rounded-full px-1.5 py-0.5">{pending.length}</span>}
            </TabsTrigger>
          </TabsList>
          <Button size="sm" onClick={openNew}><Plus className="w-3.5 h-3.5 mr-1" /> Add Reward</Button>
        </div>

        {/* Catalog */}
        <TabsContent value="catalog">
          {(!data?.catalog || data.catalog.length === 0) ? (
            <div className="text-center py-16 text-muted-foreground border border-dashed rounded-lg">
              <Gift className="w-8 h-8 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No rewards set up yet</p>
              <p className="text-sm mb-4">Add vouchers and gift card options for staff to redeem</p>
              <Button size="sm" onClick={openNew}><Plus className="w-3.5 h-3.5 mr-1" /> Add First Reward</Button>
            </div>
          ) : (
            <div className="border border-border rounded-lg divide-y divide-border">
              {data.catalog.map(item => (
                <div key={item.id} className="flex items-center gap-4 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground">{item.name}</p>
                      {!item.active && <Badge variant="outline" className="text-xs">Inactive</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">{item.type} · {item.pointsCost} points</p>
                  </div>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(item)}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Redemptions */}
        <TabsContent value="redemptions">
          {(!data?.redemptions || data.redemptions.length === 0) ? (
            <div className="text-center py-16 text-muted-foreground">
              <ShoppingBag className="w-8 h-8 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No redemptions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.redemptions.map(r => (
                <div key={r.id} className="border border-border rounded-lg bg-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{r.rewardName}</p>
                      <p className="text-xs text-muted-foreground">{r.userName} · {r.userEmail}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{r.pointsSpent} pts · Requested {fmtDate(r.requestedAt)}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[r.status || 'Pending'] || ''}`}>
                        {r.status || 'Pending'}
                      </span>
                      {r.status !== 'Fulfilled' && r.status !== 'Cancelled' && (
                        <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => openFulfill(r)}>
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Fulfil
                        </Button>
                      )}
                    </div>
                  </div>
                  {r.voucherDetails && (
                    <div className="mt-2 bg-green-50 border border-green-200 rounded px-3 py-2">
                      <p className="text-xs text-green-700">{r.voucherDetails}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit/Create reward sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-sm overflow-y-auto">
          <SheetHeader><SheetTitle>{editItem ? 'Edit Reward' : 'New Reward'}</SheetTitle></SheetHeader>
          <div className="space-y-4 mt-6">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Name *</label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Amazon £10 Voucher" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Description</label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="min-h-16" placeholder="What staff receive when they redeem this" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Points Cost</label>
                <Input type="number" min={1} value={form.pointsCost} onChange={e => setForm(f => ({ ...f, pointsCost: parseInt(e.target.value) || 0 }))} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Type</label>
                <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gift_card">Pub Gift Card</SelectItem>
                    <SelectItem value="amazon">Amazon Voucher</SelectItem>
                    <SelectItem value="online">Online Voucher</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Image URL (optional)</label>
              <Input value={form.imageUrl} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))} placeholder="https://..." />
            </div>
            <div className="flex items-center justify-between border border-border rounded-lg px-3 py-2.5">
              <div>
                <p className="text-sm font-medium text-foreground">Active</p>
                <p className="text-xs text-muted-foreground">Visible to staff in the rewards shop</p>
              </div>
              <Switch checked={form.active} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} />
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : editItem ? 'Update Reward' : 'Create Reward'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Fulfil redemption dialog */}
      <Dialog open={fulfillOpen} onOpenChange={v => { if (!fulfilling) setFulfillOpen(v); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Process Redemption</DialogTitle></DialogHeader>
          {fulfillTarget && (
            <div className="space-y-4 pt-2">
              <div className="bg-muted/30 rounded-lg p-3 text-sm">
                <p className="font-medium">{fulfillTarget.rewardName}</p>
                <p className="text-xs text-muted-foreground">{fulfillTarget.userName} · {fulfillTarget.pointsSpent} pts</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Status</label>
                <Select value={fulfillForm.status} onValueChange={v => setFulfillForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Processing">Processing</SelectItem>
                    <SelectItem value="Fulfilled">Fulfilled</SelectItem>
                    <SelectItem value="Cancelled">Cancelled (refunds points)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Voucher Details (visible to staff)</label>
                <Textarea
                  value={fulfillForm.voucherDetails}
                  onChange={e => setFulfillForm(f => ({ ...f, voucherDetails: e.target.value }))}
                  placeholder="Voucher code, instructions, card number, PIN, etc."
                  className="min-h-20"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Balance Check URL (optional)</label>
                <Input
                  value={fulfillForm.balanceCheckUrl}
                  onChange={e => setFulfillForm(f => ({ ...f, balanceCheckUrl: e.target.value }))}
                  placeholder="https://checkbalance.example.com"
                />
                <p className="text-xs text-muted-foreground mt-1">Staff will see a "Check Balance" button linking to this URL</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Admin Notes (private)</label>
                <Input value={fulfillForm.adminNotes} onChange={e => setFulfillForm(f => ({ ...f, adminNotes: e.target.value }))} placeholder="Internal notes..." />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setFulfillOpen(false)} disabled={fulfilling}>Cancel</Button>
                <Button className="flex-1" onClick={handleFulfill} disabled={fulfilling}>
                  {fulfilling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : 'Update Redemption'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
